// Lets code that holds an editor ask for a reconcile without knowing anything
// about the binding engine.
//
// The assistant is the caller that needs this. Its edits are ordinary
// user-origin edits as far as bindings are concerned, and some of them change
// what formulas depend on - inserting a row into a configured table is the clear
// case, since an unbound row has to be adopted before its formulas mean
// anything. Left to the user's next commit, the document would sit inconsistent
// in between.
//
// Keyed by the editor instance in a WeakMap, so a torn-down editor takes its
// registration with it even if dispose never runs.

import type { ApplyRulesResult } from './core/engine';
import {
  formulaOccurrences,
  formulaScopeKey,
  getAt,
  scanBindings
} from './core/sfdtAdapter';
import { parseDisplay } from './core/valueTypes';
import type { SfdtBlock, SfdtDocument } from './core/sfdtTypes';

export type BindingCommand =
  | {
      type: 'set-value';
      name: string;
      value: string;
      tableId?: string;
      rowId?: string;
    }
  | {
      type: 'add-row';
      tableId: string;
      afterRowId: string | null;
      rowId: string;
    }
  | { type: 'remove-row'; tableId: string; rowId: string }
  | {
      type: 'add-table';
      afterTableId: string;
      afterTag: string;
      block: SfdtBlock;
    }
  | {
      type: 'replace-table';
      tableId: string;
      tag: string;
      block: SfdtBlock;
    }
  | { type: 'remove-table'; tableId: string; tag: string }
  | {
      /**
       * A formula's expression changed in a control that already exists.
       *
       * `set-value` cannot carry this and `add-table` cannot either: the first
       * moves text, the second only ever carries a WHOLE table being added or
       * removed. An expression rewritten in an existing control produced no
       * command at all, so it was dropped between the in-memory projection and
       * the live document and the two disagreed about what the document said.
       *
       * Both tags travel because the old one is the address - it is what the
       * live control still wears - and the new one is the payload.
       */
      type: 'set-expression';
      name: string;
      previousTag: string;
      tag: string;
      tableId: string | null;
      rowId: string | null;
      global: boolean;
    };

/**
 * Present only for an authored assistant batch. Ordinary commands omit this so
 * user edits and mechanical reconciliation never acquire review revisions.
 */
export interface BindingCommandProvenance {
  author: string;
  changeSetId: string;
  group: string;
}

export interface BindingCommandOptions {
  provenance?: BindingCommandProvenance;
}

export interface BindingCommandSurface {
  flush(): void;
  runCommands(
    commands: BindingCommand[],
    options?: BindingCommandOptions
  ): ApplyRulesResult;
}

const reconcilers = new WeakMap<object, BindingCommandSurface>();

export function registerBindingReconciler(
  editor: object,
  surface: BindingCommandSurface
): void {
  reconcilers.set(editor, surface);
}

export function unregisterBindingReconciler(editor: object): void {
  reconcilers.delete(editor);
}

/**
 * Reconcile this editor's bindings, if it has any. Returns whether one ran, and
 * never throws: a failed reconcile must not turn into a failed assistant edit.
 */
export function reconcileBoundDocument(editor: unknown): boolean {
  if (!editor || typeof editor !== 'object') return false;
  const surface = reconcilers.get(editor as object);
  if (!surface) return false;
  try {
    surface.flush();
    return true;
  } catch (error) {
    console.error('Feathery: reconciling document bindings failed', error);
    return false;
  }
}

export function bindingCommandSurfaceFor(
  editor: unknown
): BindingCommandSurface | null {
  if (!editor || typeof editor !== 'object') return null;
  return reconcilers.get(editor as object) ?? null;
}
export function diffBindingCommands(
  before: SfdtDocument,
  after: SfdtDocument
): BindingCommand[] {
  const previous = scanBindings(before);
  const next = scanBindings(after);
  const previousTables = [...previous.tables.keys()].sort();
  const nextTables = [...next.tables.keys()].sort();
  const commands: BindingCommand[] = [];
  const replacedTableIds = new Set<string>();

  const logicalColumnCount = (table: unknown): number => {
    const rows = (table as { rows?: Array<{ cells?: unknown[] }> } | undefined)
      ?.rows;
    if (!Array.isArray(rows)) return 0;
    return rows.reduce(
      (widest, row) =>
        Math.max(
          widest,
          (row.cells ?? []).reduce<number>(
            (width, cell) =>
              width +
              Math.max(
                1,
                Number(
                  (cell as { cellFormat?: { columnSpan?: number } })?.cellFormat
                    ?.columnSpan
                ) || 1
              ),
            0
          )
        ),
      0
    );
  };
  const rawRowCount = (table: unknown): number => {
    const rows = (table as { rows?: unknown[] } | undefined)?.rows;
    return Array.isArray(rows) ? rows.length : 0;
  };

  for (const tableId of nextTables) {
    if (previous.tables.has(tableId)) continue;
    const table = next.tables.get(tableId);
    if (!table) continue;
    const parent = table.markerPath.slice(0, -1);
    const at = Number(table.markerPath[table.markerPath.length - 1]);
    const anchor = [...previous.tables.values()]
      .filter(
        (candidate) =>
          JSON.stringify(candidate.markerPath.slice(0, -1)) ===
            JSON.stringify(parent) &&
          Number(candidate.markerPath[candidate.markerPath.length - 1]) < at
      )
      .sort(
        (a, b) =>
          Number(b.markerPath[b.markerPath.length - 1]) -
          Number(a.markerPath[a.markerPath.length - 1])
      )[0];
    if (!anchor) throw new Error(`no insertion anchor for table ${tableId}`);
    const anchorBlock = getAt(before, anchor.markerPath) as any;
    commands.push({
      type: 'add-table',
      afterTableId: anchor.tableId,
      afterTag: String(anchorBlock?.contentControlProperties?.tag || ''),
      block: getAt(after, table.markerPath) as SfdtBlock
    });
  }
  // A table-subtree replacement adds the successor beside the source, then
  // removes the source. Native application must keep that order because the
  // source control is the insertion anchor.
  for (const tableId of previousTables) {
    if (next.tables.has(tableId)) continue;
    const table = previous.tables.get(tableId);
    if (!table) continue;
    const marker = getAt(before, table.markerPath) as any;
    commands.push({
      type: 'remove-table',
      tableId,
      tag: String(marker?.contentControlProperties?.tag || '')
    });
  }

  for (const tableId of nextTables.filter((id) => previous.tables.has(id))) {
    const beforeTable = previous.tables.get(tableId);
    const afterTable = next.tables.get(tableId);
    if (!beforeTable || !afterTable) continue;
    if (!beforeTable.tablePath || !afterTable.tablePath) continue;
    const beforeColumns = logicalColumnCount(
      getAt(before, beforeTable.tablePath)
    );
    const afterColumns = logicalColumnCount(getAt(after, afterTable.tablePath));
    const beforeIds = new Set(beforeTable.rows.map((row) => row.rowId));
    const afterIds = new Set(afterTable.rows.map((row) => row.rowId));
    const sameBoundRows =
      beforeIds.size === afterIds.size &&
      [...beforeIds].every((rowId) => afterIds.has(rowId));
    const unboundRowShapeChanged =
      sameBoundRows &&
      rawRowCount(getAt(before, beforeTable.tablePath)) !==
        rawRowCount(getAt(after, afterTable.tablePath));
    if (beforeColumns !== afterColumns || unboundRowShapeChanged) {
      const marker = getAt(before, beforeTable.markerPath) as any;
      replacedTableIds.add(tableId);
      commands.push({
        type: 'replace-table',
        tableId,
        tag: String(marker?.contentControlProperties?.tag || ''),
        block: getAt(after, afterTable.markerPath) as SfdtBlock
      });
      continue;
    }
    for (const row of beforeTable.rows)
      if (row.rowId && !afterIds.has(row.rowId))
        commands.push({ type: 'remove-row', tableId, rowId: row.rowId });
    for (let i = 0; i < afterTable.rows.length; i++) {
      const row = afterTable.rows[i];
      if (row.rowId && !beforeIds.has(row.rowId))
        commands.push({
          type: 'add-row',
          tableId,
          afterRowId: i ? afterTable.rows[i - 1].rowId : null,
          rowId: row.rowId
        });
    }
    for (const row of afterTable.rows) {
      if (!row.rowId) continue;
      for (const occurrence of row.bindings.values()) {
        if (occurrence.def.kind !== 'field') continue;
        const prior = beforeTable.rows
          .find((candidate) => candidate.rowId === row.rowId)
          ?.bindings.get(occurrence.name);
        if (prior?.text === occurrence.text) continue;
        commands.push({
          type: 'set-value',
          tableId,
          rowId: row.rowId,
          name: occurrence.name,
          value: parseDisplay(occurrence.def.fieldType, occurrence.text)
        });
      }
    }
  }
  // An expression change never moves a value on its own, so compare logical
  // formula identities rather than the storage bucket or the changed tag.
  const formulaIsInsideReplacedTable = (
    occurrence: typeof previous.occurrences[number]
  ): boolean =>
    [...replacedTableIds].some((tableId) => {
      const tablePath = previous.tables.get(tableId)?.tablePath;
      return (
        !!tablePath &&
        tablePath.every(
          (segment, index) => String(occurrence.path[index]) === String(segment)
        )
      );
    });
  const formulasByScope = (index: typeof next) => {
    const scopes = new Map<string, ReturnType<typeof formulaOccurrences>>();
    for (const occurrence of formulaOccurrences(index)) {
      const key = formulaScopeKey(occurrence);
      const entries = scopes.get(key);
      if (entries) entries.push(occurrence);
      else scopes.set(key, [occurrence]);
    }
    return scopes;
  };
  const previousFormulaScopes = formulasByScope(previous);
  for (const [scope, occurrences] of formulasByScope(next)) {
    const current = occurrences[0];
    const priorOccurrences = previousFormulaScopes.get(scope) ?? [];
    const prior = priorOccurrences[0];
    if (!current || !prior) continue;
    if (current.def.kind !== 'formula' || prior.def.kind !== 'formula')
      continue;
    if (current.def.expression === prior.def.expression) continue;
    if (priorOccurrences.every(formulaIsInsideReplacedTable)) continue;
    commands.push({
      type: 'set-expression',
      name: current.name,
      previousTag: prior.tag,
      tag: current.tag,
      tableId: current.tableId,
      rowId: current.rowId,
      global: current.def.isGlobal
    });
  }
  for (const [name, occurrences] of next.fields) {
    const occurrence = occurrences[0];
    if (!occurrence || previous.fields.get(name)?.[0]?.text === occurrence.text)
      continue;
    commands.push({
      type: 'set-value',
      name,
      value: parseDisplay(occurrence.def.fieldType, occurrence.text)
    });
  }
  return commands;
}
